import { Button, Modal, Text } from '@payaca/ambrosia';
import { FC, useState } from 'react';

import { useUserHasPermission } from '@/hooks/usePermissions';
import TemplateSelectionControl from '@/ui/components/templateSelectionControl/TemplateSelectionControl';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { PublicHydratedEntityTemplate } from '@payaca/types/entity-templates';

type Props = {
  isOpen: boolean;
  /**
   * Whether the account has any Proposal templates. Used to show an explanatory
   * empty state when the user can neither pick a template nor start from
   * scratch.
   */
  hasAnyTemplates: boolean;
  /** Called with a template to apply, or no argument to start from scratch. */
  onApplyTemplate: (
    selectedTemplate?: PublicHydratedEntityTemplate<'proposal'>
  ) => void;
  onClose: () => void;
};

/**
 * Modal that lets a user pick a Proposal template or start from scratch before
 * a Proposal is created.
 */
const CreateProposalTemplateModal: FC<Props> = (props) => {
  const { isOpen, hasAnyTemplates, onApplyTemplate, onClose } = props;

  const [selectedTemplate, setSelectedTemplate] =
    useState<PublicHydratedEntityTemplate<'proposal'>>();

  const canStartFromScratch = useUserHasPermission({
    permissions: [JobPermissions.ADD_CUSTOM_JOB],
  });

  const isDeadEnd = !hasAnyTemplates && !canStartFromScratch;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create a proposal">
      <Modal.Body>
        {isDeadEnd ? (
          <Text>
            No proposal templates are available. Ask an account administrator to
            create one before adding a proposal.
          </Text>
        ) : (
          <TemplateSelectionControl<'proposal'>
            selectedTemplate={selectedTemplate}
            label="Choose a template"
            onChange={setSelectedTemplate}
            allowedEntityTypes={['proposal']}
          />
        )}
      </Modal.Body>
      {!isDeadEnd && (
        <Modal.Footer>
          <Modal.Footer.Actions>
            {canStartFromScratch && (
              <Button variant="secondary" onClick={() => onApplyTemplate()}>
                Start from scratch
              </Button>
            )}
            <Button
              disabled={!selectedTemplate}
              onClick={() => onApplyTemplate(selectedTemplate)}
            >
              Apply template
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CreateProposalTemplateModal;
